import type { PrintStyle } from './printStyle'
import offlineDB from '../indexedDB/offlineDB'

export type PrintCommand = 'ESCPOS' | 'STARLINE' | 'STARGRAPHIC' | 'STARPRNT' | 'STARIMPACT'

type CommandPrinter = {
    _id?: string
    api_key?: string
    command_language?: string | null
    command_language_verified?: string | null
    printer_type?: string
    network_port?: number
    network_address?: string
    windows_shared_name?: string
    stationId?: string
}

export function normalizePrintCommand(value: unknown): PrintCommand | null {
    const raw = String(value || '').toUpperCase().replace(/[\s/_-]/g, '')
    if (raw === 'STARIMPACT' || raw === 'IMPACT') return 'STARIMPACT'
    if (raw === 'STARPRNT' || raw === 'STARFUTUREPRNT') return 'STARPRNT'
    if (raw === 'STARGRAPHIC' || raw === 'STARGRAPHICMODE' || raw === 'GRAPHIC') return 'STARGRAPHIC'
    if (raw === 'STARLINE' || raw === 'STAR') return 'STARLINE'
    if (raw === 'ESCPOS' || raw === 'ESC') return 'ESCPOS'
    return null
}

export function isPrintCommandPending(printer?: CommandPrinter | null): boolean {
    const current = normalizePrintCommand(printer?.command_language)
    const verified = normalizePrintCommand(printer?.command_language_verified)
    if (!current || !verified) return false
    return current !== verified
}

/** Command live jobs may send. An unverified switch is not sent. */
export function activePrintCommand(printer?: CommandPrinter | null): PrintCommand {
    if (isPrintCommandPending(printer)) {
        return normalizePrintCommand(printer?.command_language_verified) || 'ESCPOS'
    }
    return normalizePrintCommand(printer?.command_language)
        || normalizePrintCommand(printer?.command_language_verified)
        || 'ESCPOS'
}

export function commandFieldsForSave(
    previous: CommandPrinter | null | undefined,
    nextCommand: PrintCommand
): { command_language: PrintCommand; command_language_verified?: PrintCommand } {
    const previousCommand = normalizePrintCommand(previous?.command_language)
    const previousVerified = normalizePrintCommand(previous?.command_language_verified)
    if (previousCommand && previousCommand !== nextCommand) {
        return {
            command_language: nextCommand,
            command_language_verified: previousVerified || previousCommand,
        }
    }
    return {
        command_language: nextCommand,
        command_language_verified: previousVerified || undefined,
    }
}

export function styleForPrintCommand(style: PrintStyle, command: PrintCommand): PrintStyle {
    if (command === 'STARGRAPHIC' || command === 'STARPRNT') {
        return { ...style, mode: 'Image', brand: 'ESCPOS', starEscpos: false }
    }
    if (command === 'STARIMPACT') {
        return { ...style, brand: 'StarImpact', starEscpos: false }
    }
    return {
        ...style,
        brand: command === 'STARLINE' ? 'StarLine' : 'ESCPOS',
        starEscpos: false,
    }
}

function printerApiKey(setting: any): string {
    return String(
        setting?.generalTab?.apikey
        || setting?.generalTab?.apiKey
        || setting?.apikey
        || setting?.api_key
        || ''
    )
}

function starEscposEnabled(value: unknown): boolean {
    return value === true || String(value).toLowerCase() === 'true'
}

/** One-time command for a printer that has none stored yet. */
export function seedPrintCommand(printer: CommandPrinter, printerSettings: any[] = []): PrintCommand {
    const existing = normalizePrintCommand(printer?.command_language)
    if (existing) return existing
    const wanted = String(printer?.api_key || '')
    const match = wanted
        ? printerSettings.find((setting) => printerApiKey(setting) === wanted)
        : null
    const settingsTab = match?.settingsTab || match?.data?.settingsTab || null
    const brand = normalizePrintCommand(settingsTab?.printer)
    if (brand === 'STARLINE' && !starEscposEnabled(settingsTab?.starEscpos)) return 'STARLINE'
    return 'ESCPOS'
}

export async function readPrintCommandCopies(printerId: string, storeId?: string | null): Promise<{
    local: PrintCommand | null
    storeCopy: PrintCommand | null
}> {
    let local: PrintCommand | null = null
    let storeCopy: PrintCommand | null = null
    try {
        const printers = await window.api.findPrinters()
        const match = (printers || []).find((printer: CommandPrinter) => printer?._id === printerId)
        local = normalizePrintCommand(match?.command_language)
    } catch (error) {
        console.warn('[Print command] Could not read the local printer', error)
    }
    if (storeId) {
        try {
            const store = await offlineDB.getMasterData('stores', storeId)
            const storeDB = (await import('../indexedDB/storeDB')).default
            await storeDB.init()
            const assigned = await storeDB.getStationId(storeId)
            const stationId = assigned?.assigned === true ? String(assigned.stationId || '').trim() : ''
            const list = store?.mevPrinters || []
            const match = stationId
                ? list.find((printer: CommandPrinter) => printer?._id === printerId && String(printer?.stationId || '') === stationId)
                : null
            storeCopy = normalizePrintCommand(match?.command_language)
        } catch (error) {
            console.warn('[Print command] Could not read the store copy', error)
        }
    }
    return { local, storeCopy }
}

/** Resolves when the print call's promise finishes. A callback is optional and must not be required. */
export function finishPrintCall(
    run: (report: (error?: unknown) => void) => Promise<void> | void
): Promise<void> {
    return new Promise((resolve, reject) => {
        let settled = false
        const finish = (error?: unknown) => {
            if (settled) return
            settled = true
            if (error) reject(error instanceof Error ? error : new Error(String(error)))
            else resolve()
        }
        try {
            const pending = run(finish)
            if (pending && typeof pending.then === 'function') {
                pending.then(() => finish()).catch((error) => finish(error))
            }
        } catch (error) {
            finish(error)
        }
    })
}

export function sendPrinterBytes(
    printer: CommandPrinter,
    data: Uint8Array
): Promise<void> {
    if (printer.printer_type === 'Network ESC/POS') {
        if (!printer.network_address) return Promise.reject(new Error('Printer address is required'))
        const port = Number(printer.network_port) || 9100
        return finishPrintCall((report) => window.api.printNetwork(
            port,
            printer.network_address as string,
            data,
            report
        ))
    }
    if (printer.printer_type === 'Windows Shared ESC/POS') {
        if (!printer.windows_shared_name) return Promise.reject(new Error('Printer name is required'))
        return finishPrintCall((report) => window.api.printSerial(
            printer.windows_shared_name as string,
            data,
            report
        ))
    }
    return Promise.reject(new Error('Unknown printer type'))
}
